import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import AboutBanner from "@/components/AboutBanner";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Footer from "@/components/Footer";
import HowItWorks from "@/components/HowItWorks";
import PricingPreview from "@/components/PricingPreview";
import AuthModal from "@/components/AuthModal";
import InstallPWAPrompt from "@/components/InstallPWAPrompt";
import StickyMobileCta from "@/components/StickyMobileCta";
import FloatingSignupCta from "@/components/FloatingSignupCta";
import VisitorNudge from "@/components/VisitorNudge";
import { useAuth } from "@/contexts/AuthContext";
import JsonLd from "@/components/JsonLd";
import Seo from "@/components/Seo";
import {
  MobileSectionsAccordion,
  MobileSectionItem,
} from "@/components/MobileSectionsAccordion";

const SITE_BASE = "https://admissioncrafts.com";

// Organization + WebSite schemas help Google build the brand panel and
// understand the site's name + search box. Kept minimal — just the basics
// Google actually uses for rich results.
const ORG_AND_SITE_SCHEMA = [
  {
    "@type": "Organization",
    "@id": `${SITE_BASE}/#organization`,
    name: "AdmissionCrafts",
    url: SITE_BASE,
    logo: `${SITE_BASE}/logo.png`,
    description:
      "MHT-CET (Maharashtra CAP) + NEET (MCC / State DME) college predictor. Built by OriginCraft Consultancy Services Pvt Ltd for students across India.",
    sameAs: [],
    foundingDate: "2025",
    areaServed: { "@type": "Country", name: "India" },
  },
  {
    "@type": "WebSite",
    "@id": `${SITE_BASE}/#website`,
    url: SITE_BASE,
    name: "AdmissionCrafts",
    publisher: { "@id": `${SITE_BASE}/#organization` },
    inLanguage: "en-IN",
  },
  {
    "@type": "BreadcrumbList",
    "@id": `${SITE_BASE}/#breadcrumb`,
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_BASE },
      { "@type": "ListItem", position: 2, name: "CAP Guide", item: `${SITE_BASE}/#cap-guide` },
    ],
  },
];

export default function Landing() {
  const [auth, setAuth] = useState({ open: false, mode: "signin", intent: null });
  const [params, setParams] = useSearchParams();
  // Read auth session so we can hide the floating signup nudge for users
  // who are already logged in (they'd see it as noise, not as conversion).
  const { session } = useAuth();

  // Auto-redirect from /api/api.js 401-interceptor lands users here with
  // ?session_expired=1. Show one toast + auto-open the sign-in modal so the
  // recovery feels like one tap, not three.
  useEffect(() => {
    if (params.get("session_expired") === "1") {
      toast.info("Your session expired — please sign in again.", { duration: 5000 });
      setAuth({ open: true, mode: "signin", intent: null });
      const next = new URLSearchParams(params);
      next.delete("session_expired");
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  const openAuth = (mode = "signup", intent = null) => {
    // Stash intent IMMEDIATELY on CTA click so it survives modal close/cancel,
    // Google OAuth redirect, or tab switches before signup completes.
    if (intent) {
      try { localStorage.setItem("dhruva_pending_intent", intent); } catch (_) { /* storage unavailable */ }
    }
    setAuth({ open: true, mode, intent });
  };
  const closeAuth = (open) => setAuth((s) => ({ ...s, open }));
  const setMode = (mode) => setAuth((s) => ({ ...s, mode }));

  // Feb 2026 v4 (req 4): AiSensy WhatsApp widget MOVED from Landing to
  // the Help section (/help/videos). Rationale — users who reach
  // /dashboard shouldn't see the WhatsApp bubble hanging around from
  // their landing visit (see VideoLibrary.jsx for the new mount).

  return (
    <div className="min-h-screen bg-white">
      <Seo
        path="/"
        title="MHT-CET College Predictor & CAP Guide"
        description="Personalised MHT-CET + NEET college predictor. Find your best-fit colleges in High Chance, Possible Chance and Low Chance buckets — backed by official past cutoffs. One-time ₹99. For students across India."
      />
      <Navbar onOpenAuth={(mode) => openAuth(mode === "signup" ? "signup" : "signin")} />

      <Hero onRegister={() => openAuth("signup")} />

      {/*
        Feb 2026 (landing revamp): The green "Doing NEET?" section and the
        Top-Colleges / Branches / CAP-Guide preview blocks have all been
        moved OUT of the landing page. NEET entry lives in the top nav
        ("NEET predictor" pill). Colleges, Branches, CAP Rounds and NEET
        Explainer now live under the "Streams" dropdown in the top nav so
        the landing itself stays a tight funnel: Hero → About → How it
        works → Pricing → Footer.
      */}

      <div className="scroll-mt-4">
        <AboutBanner variant="landing" />
      </div>

      <MobileSectionsAccordion defaultOpen="how">
        <MobileSectionItem
          id="how"
          eyebrow="Start here"
          title="How AdmissionCrafts works"
          chip="2 min read"
          preview="Three quick steps — pick your exam, enter marks, see the colleges you can get."
        >
          <HowItWorks />
        </MobileSectionItem>
        <MobileSectionItem
          id="pricing"
          eyebrow="Pricing"
          title="One-time ₹99 — no subscription"
          chip="₹99 · one-time"
          preview="What's included, what's coming in Dhruva Pro, and refund guarantees."
        >
          <PricingPreview
            onBasicCta={() => openAuth("signup")}
            onProCta={() => openAuth("signup", "dhruva_pro_waitlist")}
          />
        </MobileSectionItem>
      </MobileSectionsAccordion>

      <Footer />

      {/* Site-wide structured data — Organization + WebSite + BreadcrumbList */}
      <JsonLd id="landing-jsonld" data={ORG_AND_SITE_SCHEMA} />

      <AuthModal
        open={auth.open}
        onOpenChange={closeAuth}
        mode={auth.mode}
        onModeChange={setMode}
        intent={auth.intent}
      />

      {/* PWA install prompt — non-blocking pill at the bottom on eligible browsers. */}
      <InstallPWAPrompt />

      {/* Mobile-only sticky bottom CTA. Appears once the user scrolls past
          the hero so we never double-up with the hero's own buttons. Both
          buttons route through the exact same flows as the hero and navbar
          so behaviour stays consistent. */}
      <StickyMobileCta
        onSignup={() => openAuth("signup")}
        onHowItWorks={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth", block: "start" })}
      />
      {/* Desktop-only floating signup card — appears after 400px scroll or
          8s on page, whichever first. Hidden for logged-in users and
          while the auth modal is already open (avoid double-CTA noise). */}
      <FloatingSignupCta
        onSignup={() => openAuth("signup")}
        hidden={!!session || auth.open}
      />

      {/* Anonymous-visitor nudge — appears after 15s dwell for un-authed users
          who haven't already interacted with the auth modal. Session-dismissible. */}
      {!session && !auth.open && (
        <VisitorNudge onRegister={() => openAuth("signup")} />
      )}
    </div>
  );
}
